import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';
import { clearAdminSessionLocal, clearAllAuthLocal, markAuthBounce } from '../../utils/auth';
import './admin.css';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ to: '/admin/dashboard', label: 'Dashboard', icon: 'bi-grid-1x2-fill' }],
  },
  {
    label: 'People',
    items: [
      { to: '/admin/doctors', label: 'Doctors', icon: 'bi-heart-pulse-fill' },
      { to: '/admin/users', label: 'Patients', icon: 'bi-people-fill' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/appointments', label: 'Appointments', icon: 'bi-calendar2-check-fill' },
      { to: '/admin/payments', label: 'Payments', icon: 'bi-wallet2' },
      { to: '/admin/earnings', label: 'Earnings', icon: 'bi-cash-stack' },
      { to: '/admin/notifications', label: 'Notifications', icon: 'bi-bell-fill' },
    ],
  },
  {
    label: 'Clinical',
    items: [
      { to: '/admin/prescriptions', label: 'Prescriptions', icon: 'bi-prescription2' },
      { to: '/admin/health-documents', label: 'Health Docs', icon: 'bi-file-earmark-medical-fill' },
    ],
  },
  {
    label: 'Content & Trust',
    items: [
      { to: '/admin/posts', label: 'Social Posts', icon: 'bi-newspaper' },
      { to: '/admin/ratings', label: 'Ratings', icon: 'bi-star-fill' },
      { to: '/admin/health-content', label: 'Waiting Room', icon: 'bi-play-btn-fill' },
    ],
  },
];

const PAGE_TITLES = {
  '/admin/dashboard': 'Dashboard Overview',
  '/admin/users': 'Patients',
  '/admin/doctors': 'Doctors',
  '/admin/profile': 'My Profile',
  '/admin/appointments': 'Appointments',
  '/admin/payments': 'Payments',
  '/admin/earnings': 'Earnings & Payouts',
  '/admin/notifications': 'Notifications',
  '/admin/prescriptions': 'Prescriptions',
  '/admin/health-documents': 'Health Documents',
  '/admin/posts': 'Social Posts',
  '/admin/ratings': 'Ratings & Reviews',
  '/admin/health-content': 'Waiting Room Content',
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 992 : false
  );
  const [search, setSearch] = useState('');

  const [username, setUsername] = useState(
    () => localStorage.getItem('adminUsername') || 'Admin'
  );
  const [avatarUrl, setAvatarUrl] = useState(
    () => localStorage.getItem('adminImage') || ''
  );
  const initial = username.slice(0, 1).toUpperCase();

  useEffect(() => {
    const sync = () => {
      setUsername(localStorage.getItem('adminUsername') || 'Admin');
      setAvatarUrl(localStorage.getItem('adminImage') || '');
    };
    window.addEventListener('admin-profile-updated', sync);
    return () => window.removeEventListener('admin-profile-updated', sync);
  }, []);

  const logout = async () => {
    try {
      await api.post('admin/logout/');
    } catch {
      /* ignore */
    }
    markAuthBounce();
    clearAdminSessionLocal();
    clearAllAuthLocal();
    localStorage.removeItem('adminUsername');
    localStorage.removeItem('adminImage');
    navigate('/admin/login', { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return;
    if (q.includes('doctor')) navigate('/admin/doctors');
    else if (q.includes('patient') || q.includes('user')) navigate('/admin/users');
    else if (q.includes('appoint') || q.includes('booking')) navigate('/admin/appointments');
    else if (q.includes('pay')) navigate('/admin/payments');
    else if (q.includes('earn') || q.includes('payout')) navigate('/admin/earnings');
    else if (q.includes('prescrip')) navigate('/admin/prescriptions');
    else if (q.includes('rating') || q.includes('review')) navigate('/admin/ratings');
    else if (q.includes('post') || q.includes('social')) navigate('/admin/posts');
    else if (q.includes('wait')) navigate('/admin/health-content');
    else navigate(`/admin/users`);
  };

  const title = PAGE_TITLES[location.pathname]
    || (location.pathname.match(/^\/admin\/doctors\/[^/]+\/reviews/) ? 'Doctor Reviews'
      : location.pathname.startsWith('/admin/users/') ? 'Patient Profile'
        : location.pathname.startsWith('/admin/doctors/') ? 'Doctor Profile'
          : 'Admin');

  return (
    <div className="admin-shell">
      <div
        className={`admin-overlay d-lg-none ${collapsed ? '' : 'show'}`}
        onClick={() => setCollapsed(true)}
      />

      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-mark">
            <i className="bi bi-heart-pulse-fill" />
          </div>
          <div>
            <h1>FITNOVA</h1>
            <p>Super Admin</p>
          </div>
        </div>

        <nav className="admin-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="admin-nav-label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => {
                    if (window.innerWidth < 992) setCollapsed(true);
                  }}
                >
                  <i className={`bi ${item.icon}`} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-foot">
          <button type="button" className="admin-logout-btn" onClick={logout}>
            <i className="bi bi-box-arrow-left" />
            Logout
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-icon-btn"
            onClick={() => setCollapsed((v) => !v)}
            aria-label="Toggle sidebar"
          >
            <i className="bi bi-list fs-5" />
          </button>

          <form className="admin-search d-none d-md-block" onSubmit={onSearch}>
            <i className="bi bi-search" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search doctors, patients, appointments..."
            />
          </form>

          <div className="ms-auto d-flex align-items-center gap-2">
            <Link
              to="/admin/profile"
              className="admin-avatar text-decoration-none"
              style={{ width: 38, height: 38, fontSize: '0.9rem' }}
              title="My profile"
            >
              {avatarUrl ? <img src={avatarUrl} alt="" /> : initial}
            </Link>
          </div>
        </header>

        <div className="admin-content">
          <div className="d-md-none mb-3">
            <form className="admin-search" onSubmit={onSearch} style={{ maxWidth: 'none' }}>
              <i className="bi bi-search" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
              />
            </form>
          </div>
          <Outlet context={{ pageTitle: title, username }} />
        </div>
      </div>
    </div>
  );
}
